import { Box, Typography } from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import JobCard from "../jobs/JobCard";
import { useAppContext } from "../../../../2_ksbHub/context/appContext";
import { useEventManagerContext } from "../../context/eventContext";
import { memo } from "react";
import KsbActionButton from "../../../../2_ksbHub/components/KsbActionButton";

import useShiftCardLogic from "../../hooks/useShiftCardLogic";

function ShiftCard({
  shift,
  jobs,
  angeboteByJobId,
  onReserve,
  viewMode,
  onEditJob,
  onDeleteJob,
  onEditShift,
  onDeleteShift,
}) {
  const theme = useTheme();

  const { currentUser, isATeacher, isAStudent, isTeacherView, isMobile } =
    useAppContext();

  const { isAnEventManagerAdmin } = useEventManagerContext();

  const {
    expanded,
    setExpanded,
    sortedJobs,
    filledSlots,
    totalSlots,
    openLeitungCount,
    openStudentCount,
    hasMyReservation,
    shouldRender,
  } = useShiftCardLogic({
    jobs,
    angeboteByJobId,
    viewMode,
    currentUser,
  });

  const isComplete = filledSlots === totalSlots;

  if (!shouldRender) return null;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: theme.shape.borderRadius,
        backgroundColor:
          viewMode === "mine" && hasMyReservation
            ? `rgba(82,133,228,0.2)`
            : theme.palette.background.paper,
      }}
    >
      {/* HEADER */}
      <Box
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          cursor: "pointer",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: { xs: "space-between", md: "flex-start" },
          gap: 1,
          mb: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            flex: { xs: 1, md: "unset" },
            minWidth: 0,
            fontWeight: 700,

            display: "flex",
            alignItems: "center",

            opacity: filledSlots === totalSlots ? 0.65 : 1,

            borderLeft:
              filledSlots === totalSlots
                ? `3px solid ${theme.palette.eventManager.slot.reserved}`
                : "3px solid transparent",

            pl: 1, // ✅ important → space after border
          }}
        >
          {shift.label}
          {shift.time !== "-" && ` (${shift.time})`}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {isComplete ? (
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 700,
                color: "success.main",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              {!isMobile ? "✅ vollständig besetzt" : "✅"}
            </Typography>
          ) : (
            <>
              {(isAnEventManagerAdmin || isATeacher) &&
                openLeitungCount > 0 && (
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
                    ({openLeitungCount} LP)
                  </Typography>
                )}

              {(isAnEventManagerAdmin || isAStudent) &&
                openStudentCount > 0 && (
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
                    ({openStudentCount} SuS)
                  </Typography>
                )}
            </>
          )}
        </Box>

        <Typography sx={{ opacity: 0.6 }}>{expanded ? "▾" : "▸"}</Typography>
      </Box>

      {/* JOBS */}
      {expanded && (
        <>
          <Box>
            {sortedJobs.length === 0 ? (
              <Typography variant="body2" sx={{ opacity: 0.6 }}>
                Keine Einträge
              </Typography>
            ) : (
              sortedJobs.map((job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  angebote={angeboteByJobId[job.id] || []}
                  onReserve={onReserve}
                  viewMode={viewMode}
                  onEdit={onEditJob}
                  onDelete={onDeleteJob}
                />
              ))
            )}
          </Box>

          {/* ADMIN ACTIONS */}
          {isAnEventManagerAdmin && !isTeacherView && (
            <Box
              sx={{
                mt: 1.5,
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
              }}
            >
              <KsbActionButton
                onClick={() => onEditShift?.(shift)}
                icon={<Edit />}
                label="Schicht Bearbeiten"
                variant="contained"
                sx={{
                  bgcolor: theme.palette.primary.main,
                  "&:hover": { bgcolor: theme.palette.primary.dark },
                }}
              />

              <KsbActionButton
                onClick={() => onDeleteShift(shift.id)}
                icon={<Delete />}
                label="Löschen"
                variant="contained"
                confirm={{
                  title: "Delete item?",
                  message: "Are you sure you want to delete this item?",
                }}
                sx={{
                  bgcolor: theme.palette.error.main,
                  color: theme.palette.error.contrastText,
                  "&:hover": { bgcolor: theme.palette.error.dark },
                }}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

export default memo(ShiftCard);
